// KAN-283 (Часть 2): глобальный сигнал «backend временно недоступен».
//
// Активируется axios-интерцептором (src/lib/axiosClient.ts) на 502/503/504 или
// сетевой ошибке по /api и ПОДТВЕРЖДАЕТСЯ пингом /api/health — чтобы не мигать
// оверлеем на одиночном блипе (напр. оборванный in-flight-запрос во время нашего
// же zero-downtime rollout, когда health на самом деле жив). Как только health
// вернулся — оверлей скрывается и React Query инвалидируется для рефетча.
//
// Store сделан внешним (useSyncExternalStore) и БЕЗ статического импорта axios/
// query-client, чтобы не поймать циклическую зависимость с axiosClient. /health
// пингуем через fetch (не через инстанс api — иначе зациклим интерцептор).
import { apiUrl } from "@/shared/api/config";

let down = false;
let polling = false;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function setDown(next: boolean) {
  if (down === next) return;
  down = next;
  emit();
}

const HEALTH_URL = `${apiUrl}/health`;
const POLL_MS = 3000;
const HEALTH_TIMEOUT_MS = 5000;

export async function isBackendHealthy(): Promise<boolean> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), HEALTH_TIMEOUT_MS);
  try {
    const res = await fetch(HEALTH_URL, {
      method: "GET",
      credentials: "include",
      cache: "no-store",
      signal: ctrl.signal,
    });
    return res.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

async function pollUntilRecovered() {
  if (polling) return;
  polling = true;
  try {
    // Первый успешный health → это был одиночный блип, оверлей НЕ показываем.
    // Первый неуспех → показываем оверлей и держим до восстановления.
    for (;;) {
      if (await isBackendHealthy()) {
        setDown(false);
        // Единственная в проекте инвалидация без ключа — и это осознанно:
        // бэкенд лежал, значит устарело всё серверное состояние сразу, и
        // перечислять ключи бессмысленно. В любом другом месте инвалидация
        // без ключа означала бы «не знаю, что менял» (KAN-990).
        try {
          const { getQueryClient } = await import("@/shared/query-client");
          getQueryClient().invalidateQueries();
        } catch {
          /* query-client недоступен — не критично */
        }
        return;
      }
      setDown(true);
      await new Promise((r) => setTimeout(r, POLL_MS));
    }
  } finally {
    polling = false;
  }
}

/** Вызывается axios-интерцептором при подозрении на недоступность backend. */
export function notifyApiOutage() {
  if (typeof window === "undefined") return;
  void pollUntilRecovered();
}

// ── useSyncExternalStore API ─────────────────────────────────────────────────
export function subscribeMaintenance(cb: () => void) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
export function getMaintenanceSnapshot() {
  return down;
}
export function getMaintenanceServerSnapshot() {
  return false;
}
